import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { trackDqErrorShown } from "@/features/analytics/trackers/dq-error-shown";
import { trackGender } from "@/features/analytics/trackers/patient-profile-captured";
import { AGE_LIMIT, GENDER } from "@/shared/models";
import { useSettingsStore } from "@/shared/stores";
import { errorToast } from "@/shared/ui";
import { calculateAgeByDOB, isValidDOB } from "@/shared/utils";
import { funnelIntakeScreensQuery } from "../api";
import { FunnelChoiceCard, FunnelSelect, FunnelStep } from "../components";
import { funnelIntakeParams } from "../hooks";
import { FUNNEL_ROUTE } from "../router/constants";
import { useFunnelStore } from "../stores";
import { firstIntakeRoute, indexQuestions } from "../utils";

const getMonthOptions = () =>
  [
    t`January`,
    t`February`,
    t`March`,
    t`April`,
    t`May`,
    t`June`,
    t`July`,
    t`August`,
    t`September`,
    t`October`,
    t`November`,
    t`December`,
  ].map((label, index) => ({ value: String(index + 1), label }));

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

// GLP-1 is only prescribed to adults 18+, so the newest selectable birth year is
// the current year minus 18. Older years stay selectable past AGE_LIMIT.MAX so
// over-age patients can enter their real DOB and see why they can't continue.
const MAX_BIRTH_YEAR = new Date().getFullYear() - AGE_LIMIT.MIN;
const YEAR_OPTIONS = Array.from({ length: 80 }, (_, i) => ({
  value: String(MAX_BIRTH_YEAR - i),
  label: String(MAX_BIRTH_YEAR - i),
}));

// Store the stable GENDER value (never the translated label) so downstream
// gender logic doesn't depend on the active locale.
const getSexOptions = () => [
  { value: GENDER.FEMALE, label: t`Female` },
  { value: GENDER.MALE, label: t`Male` },
];

export const PersonalInfo = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const language = useSettingsStore((store) => store.language);
  const [isLoadingIntake, setIsLoadingIntake] = useState(false);
  const set = useFunnelStore((store) => store.set);
  const initialWeight = useFunnelStore((store) => store.initialWeight);
  const [month, setMonth] = useState(
    useFunnelStore.getState().dateOfBirth?.month ?? "",
  );
  const [day, setDay] = useState(
    useFunnelStore.getState().dateOfBirth?.day ?? "",
  );
  const [year, setYear] = useState(
    useFunnelStore.getState().dateOfBirth?.year ?? "",
  );
  const [sex, setSex] = useState(useFunnelStore.getState().sex ?? "");

  const dob =
    month !== "" && day !== "" && year !== ""
      ? { year: Number(year), month: Number(month), day: Number(day) }
      : null;
  const dobValid = dob !== null && isValidDOB(dob);
  const age = dob !== null && dobValid ? calculateAgeByDOB(dob) : null;
  const getDobError = () => {
    if (dob === null) {
      return null;
    }
    if (age === null) {
      return t`Please enter a valid date of birth.`;
    }
    if (age < AGE_LIMIT.MIN) {
      return t`You must be at least 18 years old.`;
    }
    if (age > AGE_LIMIT.MAX) {
      return t`levelsRx treatment is available for patients between ${AGE_LIMIT.MIN} and ${AGE_LIMIT.MAX} years old.`;
    }
    return null;
  };
  const dobError = getDobError();
  const isAgeOutOfRange =
    initialWeight !== undefined &&
    age !== null &&
    (age < AGE_LIMIT.MIN || age > AGE_LIMIT.MAX);

  useEffect(() => {
    if (isAgeOutOfRange) {
      trackDqErrorShown("personal-info");
    }
  }, [isAgeOutOfRange]);

  if (initialWeight === undefined) {
    return <Navigate to={FUNNEL_ROUTE.MEASUREMENTS} replace />;
  }

  const isComplete = age !== null && dobError === null && sex !== "";

  const handleSubmit = async () => {
    if (!isComplete) {
      return;
    }
    set({ dateOfBirth: { month, day, year }, sex });
    trackGender(sex);

    const params = funnelIntakeParams(useFunnelStore.getState());
    if (!params) {
      navigate(FUNNEL_ROUTE.MEASUREMENTS);
      return;
    }

    setIsLoadingIntake(true);
    try {
      const { steps } = await queryClient.fetchQuery(
        funnelIntakeScreensQuery(language, params),
      );

      if (steps.length === 0) {
        errorToast(t`We couldn't load your questions. Please try again.`);
        return;
      }

      navigate(
        firstIntakeRoute(
          steps,
          useFunnelStore.getState().intakeAnswers ?? {},
          indexQuestions(steps),
        ),
      );
    } catch {
      errorToast(t`We couldn't load your questions. Please try again.`);
    } finally {
      setIsLoadingIntake(false);
    }
  };

  return (
    <FunnelStep
      title={t`Tell us about yourself.`}
      description={t`GLP-1 prescriptions require age verification and may involve pregnancy screening.`}
      footerNote={t`Why we ask: GLP-1 medications are only prescribed to adults 18+. Biological sex determines pregnancy-related safety screening.`}
      nextDisabled={!isComplete}
      isSubmitting={isLoadingIntake}
      onSubmit={() => void handleSubmit()}
    >
      <div className="grid gap-x-6 gap-y-5 sm:grid-cols-3">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Date of birth`}
          </span>
          <FunnelSelect
            value={month}
            onChange={setMonth}
            options={getMonthOptions()}
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Day`}
          </span>
          <FunnelSelect value={day} onChange={setDay} options={DAY_OPTIONS} />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-primary-950">
            {t`Year`}
          </span>
          <FunnelSelect
            value={year}
            onChange={setYear}
            options={YEAR_OPTIONS}
          />
        </label>
      </div>

      {dobError && (
        <p className="mt-2 text-sm font-medium text-red-700">{dobError}</p>
      )}

      <p className="mt-6 mb-3 text-sm font-medium text-primary-950">
        {t`Sex assigned at birth`}
      </p>
      <div className="grid gap-3">
        {getSexOptions().map((option) => (
          <FunnelChoiceCard
            key={option.value}
            type="radio"
            selected={sex === option.value}
            onSelect={() => setSex(option.value)}
            label={option.label}
          />
        ))}
      </div>
    </FunnelStep>
  );
};
